import React, { useEffect, useState, useMemo, FC } from 'react';
import axios from 'axios';
import { StyledCellText } from 'molecules/Table/styles/Text.styles';
import Table from 'molecules/Table';
import {
  StyledCellContainer,
  StyledTableContainer,
} from 'molecules/Table/styles';
import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import { StyledTablePagination } from 'molecules/Table/styles/Pagination.styles';
import TablePaginationActions from 'molecules/Table/TablePagination/Actions';
import ReportPreview from 'molecules/PrjReportPreview';
import { IReportParams } from '../types';
import { capitalizeFirstLetter, humanizeEnum } from 'utils/helpers/string';

export interface SpaOrdersResponse {
  count: number;
  orders: SpaOrder[];
}

export interface SpaOrder {
  id: string;
  name: string;
  platform_type: string;
  scan_type: string;
  status: string;
  company: {
    id: number;
    name: string;
  };

  created_at: string;
  updated_at: string;

  user_id: string;
  user_spa_id: string;

  scans: Scan[];
}

export interface Scan {
  id: string;
  scan_number: number;
  status: string;

  gcp_file_url: string;
  spa_order_id: string;

  created_at: string;
  updated_at: string;
}

export const spaDescriptionText = `Some cyber threats are obvious. Others aren’t. Zazmic’s hands-on Security Posture Audit uncovers them and gives you a clear, simple plan to stay protected`;

const REPORT_STATUS_COLORS = {
  new: '#1976D2', // Blue — new / informational
  in_progress: '#ED6C02', // Orange — active / ongoing
  completed: '#2E7D32', // Green — success
  cancelled: '#9E9E9E', // Grey — inactive / neutral
  deleted: '#D32F2F', // Red — destructive
};

// if userId is provided, it will fetch the report for a specific user
const SecurityReport: FC<{ userId?: string }> = ({ userId }) => {
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(0);
  const [reports, setReports] = useState<SpaOrdersResponse>({
    count: 0,
    orders: [],
  });
  const [loader, setLoader] = useState<boolean>(false);
  const [syncing, setSyncing] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [orgReportPreview, setOrgReportPreview] = useState<SpaOrder | null>(
    null
  );

  const formatMonthYear = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
    });

  const columns = useMemo(
    () => [
      {
        key: 'month',
        label: 'Month',
        sortable: false,
        width: '20%',
        render: ({ created_at }: SpaOrder) => (
          <StyledCellText $textType="bold">
            {formatMonthYear(created_at).replace(' ', ', ')}
          </StyledCellText>
        ),
      },
      ...(!userId
        ? [
            {
              key: 'order_name',
              label: 'Order Name',
              sortable: false,
              width: '25%',
              render: ({ name }: SpaOrder) => (
                <StyledCellText
                  $textType="default"
                  sx={{ wordBreak: 'break-word' }}
                >
                  {capitalizeFirstLetter(name)}
                </StyledCellText>
              ),
            },
          ]
        : []),
      ...(!userId
        ? [
            {
              key: 'company_name',
              label: 'Company Name',
              sortable: false,
              width: '20%',
              render: ({ company }: SpaOrder) => (
                <StyledCellText $textType="default">
                  {capitalizeFirstLetter(company.name)}
                </StyledCellText>
              ),
            },
          ]
        : []),
      {
        key: 'status',
        label: 'Status',
        sortable: false,
        width: '15%',
        render: ({ status }: SpaOrder) => (
          <StyledCellText
            $textType="default"
            $color={
              REPORT_STATUS_COLORS[status as keyof typeof REPORT_STATUS_COLORS]
            }
          >
            {humanizeEnum(status)}
          </StyledCellText>
        ),
      },
      {
        key: 'report',
        label: 'Report',
        sortable: false,
        width: '30%',
        render: (order: SpaOrder) => (
          <StyledCellContainer $hStack $left $gap="15px">
            <Button disabled size="small" sx={{ maxWidth: '120px' }}>
              Pay
            </Button>

            <Button
              onClick={() => setOrgReportPreview(order)}
              variant="text"
              size="small"
              sx={{ maxWidth: '120px' }}
              disabled={order.status !== 'completed'}
            >
              View Report
            </Button>
          </StyledCellContainer>
        ),
      },
    ],
    [userId]
  );

  const handleChangePage = (event: unknown, newPage: number) => {
    setLoader(true);
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (option: number) => {
    setLoader(true);
    setCurrentPage(0);
    setRowsPerPage(option);
  };

  const handleSync = () => {
    setSyncing(true);
    let url = `${process.env.REACT_APP_USER_SERVICE}/orders/spa/sync`;
    if (userId)
      url = `${process.env.REACT_APP_USER_SERVICE}/orders/spa/${userId}/sync`;

    axios.post(url).finally(() => {
      setSyncing(false);
    });
  };

  useEffect(() => {
    (() => {
      setLoader(true);
      const params: IReportParams = {
        limit: rowsPerPage,
        page: currentPage + 1,
      };

      let url = `${process.env.REACT_APP_USER_SERVICE}/orders/spa`;
      if (userId) url = `${url}/${userId}`;

      axios
        .get(url, { params })
        .then((p) => {
          setReports(p.data);
        })
        .finally(() => {
          setLoader(false);
        });
    })();
  }, [userId, currentPage, rowsPerPage]);

  async function spaLogin() {
    try {
      setLoading(true);
      const { data } = await axios.post(
        `${process.env.REACT_APP_USER_SERVICE}/spa/login`
      );
      document.cookie =
        `token=${data.access_token}; ` +
        `Max-Age=${data.expires_in}; ` +
        `Domain=zazmic.com; ` +
        `Path=/; ` +
        `Secure; ` +
        `SameSite=None`;

      window.open('https://security.zazmic.com/dashboard', '_blank');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Stack sx={{ m: 2, gap: 2 }}>
      {/* <OverviewSummary
        totalAccounts={summaryData.totalAccounts}
        actualCost={summaryData.actualCost}
        potentialCost={summaryData.potentialCost}
        savingsPercentage={summaryData.savingsPercentage}
      /> */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack>
          <Typography
            variant="body2"
            fontSize="12px"
            sx={{ px: 2, fontWeight: 'bold' }}
          >
            See Where Your Business Is at Risk!
          </Typography>
          <Typography variant="body2" fontSize="12px" sx={{ px: 2 }}>
            {spaDescriptionText}
          </Typography>
        </Stack>
        <Stack direction="row" gap={1}>
          <Button
            variant="contained"
            size="small"
            disabled={syncing}
            onClick={handleSync}
            sx={{ width: '120px', p: 0, fontSize: '12px' }}
          >
            {syncing ? 'Syncing...' : 'Sync Reports'}
          </Button>
          <Button
            variant="contained"
            size="small"
            sx={{ width: '140px' }}
            onClick={() => spaLogin()}
          >
            {loading ? (
              <Stack
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                <CircularProgress size={18} color="secondary" thickness={3} />
                <Typography
                  sx={{
                    ml: 1,
                    fontSize: 12,
                    fontWeight: 500,
                    color: 'rgba(0, 0, 0, 0.26)',
                  }}
                >
                  loading...
                </Typography>
              </Stack>
            ) : (
              'Order Report'
            )}
          </Button>
        </Stack>
      </Stack>
      <StyledTableContainer style={{ minWidth: 800 }}>
        <Table
          idKey="id"
          loading={loader}
          columns={columns}
          data={reports.orders || []}
        />
        <StyledTablePagination
          labelDisplayedRows={() => null}
          rowsPerPageOptions={[]}
          count={reports?.count || 0}
          rowsPerPage={rowsPerPage}
          page={currentPage}
          onPageChange={handleChangePage}
          ActionsComponent={(props) => (
            <TablePaginationActions
              {...props}
              labelRowsPerPage="Items per page"
              rowsPerPageOptions={[20, 50, 100, 200]}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        />
      </StyledTableContainer>

      {orgReportPreview && (
        <ReportPreview
          report={orgReportPreview}
          onClose={() => {
            document.body.setAttribute('id', '');
            setOrgReportPreview(null);
          }}
        />
      )}
    </Stack>
  );
};

export default SecurityReport;
